/**
 * Centralized message types for the user-guide system
 * This file contains all message types used in communication
 * between components of the user guide system.
 */

// ============================================================================
// MESSAGE TYPE ENUMS
// ============================================================================

/**
 * Main communication messages between components
 */
export enum CommunicationMessageType {
  REDIRECT = 'GETDEMO_REDIRECT',
  SET_CURRENT_URL = 'GETDEMO_SET_CURRENT_URL',
  GET_CURRENT_URL = 'GETDEMO_GET_CURRENT_URL',
  RUN_GUIDE = 'GETDEMO_RUN_GUIDE',
  RUN_SURVEY = 'GETDEMO_RUN_SURVEY',
  RUN_CHECKLIST = 'GETDEMO_RUN_CHECKLIST',
  RUN_POPUP = 'GETDEMO_RUN_POPUP',
  DESTROY = 'GETDEMO_DESTROY',
  GUIDE_COMPLETED = 'GETDEMO_GUIDE_COMPLETED'
}

/**
 * Guide execution control messages
 */
export enum GuideControlMessageType {
  RUNNING_GUIDE = 'GETDEMO_RUNNING_GUIDE',
  RUNNING_SURVEY = 'GETDEMO_RUNNING_SURVEY',
  RUNNING_POPUP = 'GETDEMO_RUNNING_POPUP',
  INITIALIZED_GUIDE = 'GETDEMO_INITIALIZED_GUIDE',
  INITIALIZED_CHECKLIST = 'GETDEMO_INITIALIZED_CHECKLIST',
  CHECK_RUNNING_GUIDE = 'GETDEMO_CHECK_RUNNING_GUIDE',
  HOST_COLOR_SCHEME_CHANGED = 'GETDEMO_HOST_COLOR_SCHEME_CHANGED'
}

/**
 * Messages related to DOM elements
 */
export enum ElementMessageType {
  GET_ELEMENT_DIMENSIONS = 'GETDEMO_GET_ELEMENT_DIMENSIONS',
  SET_ELEMENT_DIMENSIONS = 'GETDEMO_SET_ELEMENT_DIMENSIONS',
  POSITION_CHANGED = 'GETDEMO_POSITION_CHANGED',
  ELEMENT_LOST = 'GETDEMO_ELEMENT_LOST',
  RESIZE_AND_POSITION_IFRAME = 'GETDEMO_RESIZE_AND_POSITION_IFRAME',
  RESIZE_AND_POSITION_CHECKLIST_IFRAME = 'GETDEMO_RESIZE_AND_POSITION_CHECKLIST_IFRAME',
  CLOSE_CHECKLIST = 'GETDEMO_CLOSE_CHECKLIST',
  CLOSE_POPUP = 'GETDEMO_CLOSE_POPUP',
  CLOSE_SURVEY = 'GETDEMO_CLOSE_SURVEY',
  START_CHECKLIST_DRAG = 'GETDEMO_START_CHECKLIST_DRAG',
  END_CHECKLIST_DRAG = 'GETDEMO_END_CHECKLIST_DRAG'
}

/**
 * Navigation messages between steps
 */
export enum NavigationMessageType {
  GO_TO_NEXT_STEP = 'GETDEMO_GO_TO_NEXT_STEP_ACTION',
  GO_TO_NEXT_STEP_SUCCESS = 'GETDEMO_GO_TO_NEXT_STEP_SUCCESS',
  SCROLL_TO_ELEMENT = 'GETDEMO_SCROLL_TO_ELEMENT'
}

/**
 * Communication messages between iframes
 */
export enum IframeMessageType {
  CHECK_IF_PARENT_HAS_SCRIPT = 'GETDEMO_CHECK_IF_PARENT_HAS_SCRIPT',
  PARENT_HAS_SCRIPT = 'GETDEMO_PARENT_HAS_SCRIPT',
  GET_IFRAME_ELEMENT_DIMENSIONS = 'GETDEMO_GET_IFRAME_ELEMENT_DIMENSIONS',
  SET_IFRAME_ELEMENT_DIMENSIONS = 'GETDEMO_SET_IFRAME_ELEMENT_DIMENSIONS',
  GET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT = 'GETDEMO_GET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT',
  SET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT = 'GETDEMO_SET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT'
}

/**
 * User identification and data messages
 */
export enum UserMessageType {
  IDENTIFY_USER = 'GETDEMO_IDENTIFY_USER',
  IDENTIFY_USER_SUCCESS = 'GETDEMO_IDENTIFY_USER_SUCCESS'
}

/**
 * Trigger and external action messages
 */
export enum TriggerMessageType {
  TRIGGER_ITEM = 'GETDEMO_TRIGGER_ITEM',
  OPEN_LINK = 'GETDEMO_OPEN_LINK'
}

/**
 * Messages related to click capture
 */
export enum ClickCaptureMessageType {
  SHOW_CLICK_CAPTURE = 'GETDEMO_SHOW_CLICK_CAPTURE',
  HIDE_CLICK_CAPTURE = 'GETDEMO_HIDE_CLICK_CAPTURE'
}

/**
 * Hotspot layer messages. Hotspots live outside the guide/popup/survey
 * lifecycle: the beacons are host DOM in a shadow root and only the tooltip
 * lives in the `/hs/:companyId` iframe, so they need their own channel.
 */
export enum HotspotMessageType {
  INITIALIZED_HOTSPOTS = 'GETDEMO_INITIALIZED_HOTSPOTS',
  SET_HOTSPOT_TARGETS = 'GETDEMO_SET_HOTSPOT_TARGETS',
  HOTSPOT_ENTER = 'GETDEMO_HOTSPOT_ENTER',
  HOTSPOT_LEAVE = 'GETDEMO_HOTSPOT_LEAVE',
  RESIZE_AND_POSITION_HOTSPOT_IFRAME = 'GETDEMO_RESIZE_AND_POSITION_HOTSPOT_IFRAME',
  HOTSPOT_DISMISSED = 'GETDEMO_HOTSPOT_DISMISSED'
}

/**
 * Banner layer messages. Like hotspots, banners are a passive surface outside
 * the guide/popup/survey queue, rendered in their own `/bn/:companyId` iframe,
 * and stay on screen while any of those run.
 */
export enum BannerMessageType {
  INITIALIZED_BANNERS = 'GETDEMO_INITIALIZED_BANNERS',
  RESIZE_AND_POSITION_BANNER_IFRAME = 'GETDEMO_RESIZE_AND_POSITION_BANNER_IFRAME'
}

// ============================================================================
// UNION TYPE FOR ALL MESSAGE TYPES
// ============================================================================

export type MessageType =
  | CommunicationMessageType
  | GuideControlMessageType
  | ElementMessageType
  | NavigationMessageType
  | IframeMessageType
  | UserMessageType
  | TriggerMessageType
  | HotspotMessageType
  | BannerMessageType;

// ============================================================================
// INTERFACES FOR MESSAGES WITH SPECIFIC PAYLOAD
// ============================================================================

/**
 * Interface for messages that set current URL
 */
export interface SetCurrentUrlMessage {
  type: CommunicationMessageType.SET_CURRENT_URL;
  url: string;
}

/**
 * Interface for user identification messages
 */
export interface IdentifyUserMessage {
  type: UserMessageType.IDENTIFY_USER;
  id?: string;
  params?: Record<string, any>;
  runId?: string;
  userData?: any;
}

/**
 * Interface for successful identification messages
 */
export interface IdentifyUserSuccessMessage {
  type: UserMessageType.IDENTIFY_USER_SUCCESS;
  runId?: string;
  user: any;
}

/**
 * Interface for item trigger messages
 */
export interface TriggerItemMessage {
  type: TriggerMessageType.TRIGGER_ITEM;
  id: string;
}

/**
 * Interface for link opening messages
 */
export interface OpenLinkMessage {
  type: TriggerMessageType.OPEN_LINK;
  href: string;
}

/**
 * Interface for guide completed messages
 */
export interface GuideCompletedMessage {
  type: CommunicationMessageType.GUIDE_COMPLETED;
  guideId: string;
}

/**
 * Interface for element dimension messages
 */
export interface SetElementDimensionsMessage {
  type: ElementMessageType.SET_ELEMENT_DIMENSIONS;
  dimensions: {
    x: number;
    y: number;
    width: number;
    height: number;
    containerWidth?: number;
    containerHeight?: number;
  };
}

/**
 * Interface for element dimension retrieval messages
 */
export interface GetElementDimensionsMessage {
  type: ElementMessageType.GET_ELEMENT_DIMENSIONS;
  paths?: any[];
  customPath?: string;
  highlight?: boolean;
  highlightColor?: string;
  nextStepButtonType?: any;
  dimBackground?: boolean;
}

/**
 * Interface for iframe resize and positioning messages
 */
export interface ResizeAndPositionIframeMessage {
  type: ElementMessageType.RESIZE_AND_POSITION_IFRAME;
  position: {
    top: string;
    left: string;
  };
  dimensions: {
    width: string;
    height: string;
  };
  show?: boolean;
  hasBackdrop?: boolean;
  closeOnOutsideClick?: boolean;
}

/**
 * Interface for checklist iframe resize and positioning messages
 */
export interface ResizeAndPositionChecklistIframeMessage {
  type: ElementMessageType.RESIZE_AND_POSITION_CHECKLIST_IFRAME;
  position: {
    top: string;
    left: string;
  };
  dimensions: {
    width: string;
    height: string;
  };
}

/**
 * Interface for next step messages
 */
export interface GoToNextStepMessage {
  type: NavigationMessageType.GO_TO_NEXT_STEP;
  nextStepButtonType?: any;
}

/**
 * Interface for next step success messages
 */
export interface GoToNextStepSuccessMessage {
  type: NavigationMessageType.GO_TO_NEXT_STEP_SUCCESS;
  shouldDestroy?: boolean;
}

/**
 * Interface for iframe dimension messages
 */
export interface SetIframeElementDimensionsMessage {
  type: IframeMessageType.SET_IFRAME_ELEMENT_DIMENSIONS;
  dimensions: {
    x: number;
    y: number;
    width: number;
    height: number;
    isElementVisible: boolean;
  };
}

/**
 * Interface for iframe dimension messages by element
 */
export interface SetIframeElementDimensionsByElementMessage {
  type: IframeMessageType.SET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT;
  dimensions: {
    x: number;
    y: number;
    width: number;
    height: number;
    isOutOfViewport?: boolean;
    isElementVisible: boolean;
  } | null;
}

/**
 * Interface for iframe dimension retrieval messages
 */
export interface GetIframeElementDimensionsMessage {
  type: IframeMessageType.GET_IFRAME_ELEMENT_DIMENSIONS;
  paths: any[];
  nextStepButtonType?: any;
}

/**
 * Interface for iframe dimension retrieval messages by element
 */
export interface GetIframeElementDimensionsByElementMessage {
  type: IframeMessageType.GET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT;
  paths: any[];
}

/**
 * A single beacon the host layer must draw, as resolved by the player.
 */
export interface HotspotTarget {
  pointId: string;
  hotspotId: string;
  paths?: any[];
  customPath?: string;
  color: string;
  beaconSize: number;
  beaconStyle?: 'ring' | 'filled';
  positionType?: string;
}

/**
 * Interface for the full set of beacons currently eligible on this page.
 */
export interface SetHotspotTargetsMessage {
  type: HotspotMessageType.SET_HOTSPOT_TARGETS;
  targets: HotspotTarget[];
}

/**
 * Interface for the hover-in / hover-out of a beacon.
 */
export interface HotspotHoverMessage {
  type: HotspotMessageType.HOTSPOT_ENTER | HotspotMessageType.HOTSPOT_LEAVE;
  pointId: string;
}

/**
 * Interface for the tooltip iframe resize and positioning messages.
 *
 * The host owns placement: it already draws the beacon, so it resolves
 * top/left from the beacon node + authored `positionType`. The player only
 * reports the measured tooltip size (and which point is open).
 */
export interface ResizeAndPositionHotspotIframeMessage {
  type: HotspotMessageType.RESIZE_AND_POSITION_HOTSPOT_IFRAME;
  pointId?: string;
  positionType?: string;
  position?: { top: string; left: string };
  dimensions: { width: string; height: string };
  show?: boolean;
}

/**
 * The banner player reports which banner it shows and its measured size; the
 * host pins the iframe to the top or bottom edge. `width` is `'100%'` for a
 * full-width strip, otherwise the card width (the iframe is then centred).
 */
export interface ResizeAndPositionBannerIframeMessage {
  type: BannerMessageType.RESIZE_AND_POSITION_BANNER_IFRAME;
  bannerId?: string;
  placement?: 'TOP' | 'BOTTOM';
  dimensions: { width: string; height: string };
  show: boolean;
}

/**
 * Interface for a beacon the user dismissed and that must stop rendering.
 */
export interface HotspotDismissedMessage {
  type: HotspotMessageType.HOTSPOT_DISMISSED;
  pointId: string;
}

// ============================================================================
// UNION TYPE FOR ALL TYPED MESSAGES
// ============================================================================

export type TypedMessage =
  | SetCurrentUrlMessage
  | IdentifyUserMessage
  | IdentifyUserSuccessMessage
  | TriggerItemMessage
  | OpenLinkMessage
  | GuideCompletedMessage
  | SetElementDimensionsMessage
  | GetElementDimensionsMessage
  | ResizeAndPositionIframeMessage
  | ResizeAndPositionChecklistIframeMessage
  | GoToNextStepMessage
  | GoToNextStepSuccessMessage
  | SetIframeElementDimensionsMessage
  | SetIframeElementDimensionsByElementMessage
  | GetIframeElementDimensionsMessage
  | GetIframeElementDimensionsByElementMessage
  | SetHotspotTargetsMessage
  | HotspotHoverMessage
  | ResizeAndPositionHotspotIframeMessage
  | HotspotDismissedMessage
  | ResizeAndPositionBannerIframeMessage;

// ============================================================================
// GENERIC INTERFACE FOR MESSAGES
// ============================================================================

/**
 * Generic interface for all system messages
 */
export interface MessageEvent {
  type: MessageType;
  [key: string]: any;
}

// ============================================================================
// CONSTANTS FOR EASIER USAGE
// ============================================================================

/**
 * Object with all message types for easier import
 */
export const MessageTypes = {
  ...CommunicationMessageType,
  ...GuideControlMessageType,
  ...ElementMessageType,
  ...NavigationMessageType,
  ...IframeMessageType,
  ...UserMessageType,
  ...TriggerMessageType,
  ...ClickCaptureMessageType,
  ...HotspotMessageType,
  ...BannerMessageType
} as const;
